import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Wine, MapPin, Grape, FlaskConical, Ruler, Clock, DollarSign, TrendingUp, Thermometer, Lock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLanguage, getRatingTier } from "@/context/LanguageContext";
import FavoriteButton from "@/components/FavoriteButton";
import CreditWineDialog from "@/components/CreditWineDialog";
import BadgeEmbedDialog from "@/components/BadgeEmbedDialog";
import ShareWineDialog from "@/components/ShareWineDialog";
import { getRatingClasses } from "@/lib/ratingStyles";
import { getWineTypeBadgeClass } from "@/lib/wineTypeStyles";
import { useAuth } from "@/context/AuthContext";

import { API, resolveImageUrl } from "@/lib/apiBase";

export default function WineDetail() {
  const { id } = useParams();
  const [wine, setWine] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();
  const { user } = useAuth();
  const canCredit = (user?.role === "sommelier" && user?.approved) || user?.role === "admin";
  const canEmbedBadge = user?.role === "admin" || (user?.role === "partner" && user?.approved);

  useEffect(() => {
    axios.get(`${API}/wines/${id}`)
      .then(res => setWine(res.data))
      .catch(() => toast.error("Wine not found"))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) return (
    <div className="pt-24 pb-20 min-h-screen flex items-center justify-center">
      <div className="animate-pulse text-white/30 font-body">{t.loading}</div>
    </div>
  );

  if (!wine) return (
    <div className="pt-24 pb-20 min-h-screen flex items-center justify-center">
      <p className="text-white/40 font-body">{t.wineNotFound}</p>
    </div>
  );

  const registered = !!user;
  const baseDetails = [
    { icon: MapPin, label: t.region, value: wine.region },
    { icon: Wine, label: t.producer, value: wine.producer },
    { icon: Grape, label: t.vineyard, value: wine.vineyard },
    { icon: Grape, label: t.grapeVariety, value: wine.grape_variety },
    { icon: FlaskConical, label: t.alcohol, value: `${wine.alcohol_pct}%` },
    { icon: Ruler, label: t.size, value: `${wine.size_ml} ml` },
    { icon: Clock, label: t.bestDrink, value: wine.best_drink },
    { icon: Thermometer, label: t.temperature, value: wine.temperature },
  ];
  const premiumDetails = registered ? [
    { icon: DollarSign, label: t.price, value: `$${wine.price?.toFixed(2)}` },
    { icon: TrendingUp, label: t.marketValue, value: `$${wine.market_value?.toFixed(2)}` },
  ] : [];
  const details = [...baseDetails, ...premiumDetails];

  return (
    <main className="pt-24 pb-20 min-h-screen" data-testid="wine-detail-page">
      <div className="max-w-[1000px] mx-auto px-6">
        {/* Back */}
        <Link to="/catalog" className="inline-flex items-center gap-2 text-white/40 hover:text-white font-body text-sm mb-8 transition-colors" data-testid="back-to-catalog">
          <ArrowLeft className="w-4 h-4" /> {t.backToCollection}
        </Link>

        {/* Wine Header */}
        <div className="mb-12 flex flex-col sm:flex-row gap-8">
          {wine.image_url && (
            <div className="sm:w-48 h-72 shrink-0 glass-card rounded-sm flex items-center justify-center overflow-hidden" data-testid="wine-detail-image-wrap">
              <img src={resolveImageUrl(wine.image_url)} alt={wine.name} className="h-full w-auto object-contain p-4" data-testid="wine-detail-image" />
            </div>
          )}
          <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-3">
            <span className="font-accent text-xs tracking-[0.3em] text-gold/70">{wine.year}</span>
            <Badge variant="outline" className={`text-[10px] font-body tracking-wider border ${getWineTypeBadgeClass(wine.wine_type)}`}>
              {wine.wine_type?.toUpperCase()}
            </Badge>
          </div>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white/95 leading-tight mb-3 break-words min-w-0" data-testid="wine-name">
              {wine.name}
            </h1>
            <div className="flex flex-wrap items-center gap-2">
              {registered && <ShareWineDialog wine={wine} />}
              {canEmbedBadge && <BadgeEmbedDialog wineId={wine.id} wineName={wine.name} />}
              {canCredit && <CreditWineDialog wineId={wine.id} wineName={wine.name} />}
              {registered && <FavoriteButton wineId={wine.id} size={20} />}
            </div>
          </div>
          <p className="font-body text-base text-white/40">{wine.producer} &middot; {wine.region}</p>
          <div className="flex items-center gap-4 mt-4">
            <div
              className={`w-12 h-12 rounded-sm flex items-center justify-center font-display text-xl font-bold border ${getRatingClasses(wine.rating)}`}
              data-testid="wine-score"
            >
              {wine.rating}
            </div>
            <div>
              <p className="font-body text-sm text-white/50">
                {getRatingTier(wine.rating, t)}
              </p>
              <p className="font-body text-[10px] text-white/30">{t.scoreOutOf100}</p>
            </div>
          </div>
          </div>
        </div>

        <Separator className="bg-white/5 mb-10" />

        <Tabs defaultValue="details" className="w-full">
          <TabsList className="bg-wine-paper border border-white/5 rounded-sm p-1 mb-8">
            <TabsTrigger value="details" data-testid="tab-details" className="font-body text-sm data-[state=active]:bg-merlot/20 data-[state=active]:text-white rounded-sm">{t.details}</TabsTrigger>
            <TabsTrigger value="tasting" data-testid="tab-tasting" className="font-body text-sm data-[state=active]:bg-merlot/20 data-[state=active]:text-white rounded-sm">{t.tastingNotes}</TabsTrigger>
          </TabsList>

          <TabsContent value="details">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="wine-details-grid">
              {details.map(({ icon: Icon, label, value }) => (
                <div key={label} className="glass-card rounded-sm p-4 flex items-start gap-3">
                  <Icon className="w-4 h-4 text-merlot mt-0.5 shrink-0" />
                  <div>
                    <p className="font-body text-[10px] tracking-wider text-white/30 uppercase mb-0.5">{label}</p>
                    <p className="font-body text-sm text-white/80">{value}</p>
                  </div>
                </div>
              ))}
            </div>
            {registered ? (
              <div className="mt-6 glass-card rounded-sm p-5 flex items-center justify-between">
                <div>
                  <p className="font-body text-xs text-white/30 uppercase tracking-wider mb-1">{t.price}</p>
                  <p className="font-display text-3xl text-white/90">${wine.price?.toFixed(0)}</p>
                </div>
                {wine.market_value > wine.price && (
                  <div className="text-right">
                    <p className="font-body text-xs text-white/30 uppercase tracking-wider mb-1">{t.marketValue}</p>
                    <p className="font-display text-2xl text-gold">${wine.market_value?.toFixed(0)}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-6 glass-card rounded-sm p-6 text-center border-merlot/20 ring-1 ring-merlot/15" data-testid="price-locked">
                <Lock className="w-5 h-5 text-gold/70 mx-auto mb-3" />
                <p className="font-display text-lg text-white/90 mb-2">Price &amp; market value</p>
                <p className="font-body text-sm text-white/50 mb-4">
                  Pricing details are reserved for AlfredWine members. Sign in or create a free account to see live price and market value.
                </p>
                <div className="flex items-center gap-3 justify-center flex-wrap">
                  <Link to="/register">
                    <Button data-testid="wine-detail-register" className="bg-merlot hover:bg-merlot-light text-white font-body text-sm h-10 px-6 rounded-sm">
                      Create Free Account
                    </Button>
                  </Link>
                  <Link to="/login" className="font-body text-sm text-white/50 hover:text-white transition-colors" data-testid="wine-detail-signin">
                    Already a member? Sign in
                  </Link>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="tasting">
            {registered ? (
              <div className="glass-card rounded-sm p-8" data-testid="tasting-notes">
                <p className="font-accent text-xs tracking-[0.2em] text-gold/60 mb-4">{t.tastingCharacteristics}</p>
                <p className="font-body text-base text-white/70 leading-relaxed italic">
                  "{wine.tasting_notes}"
                </p>
              </div>
            ) : (
              <div className="glass-card rounded-sm p-8 text-center border-merlot/20 ring-1 ring-merlot/15" data-testid="tasting-notes-locked">
                <Lock className="w-6 h-6 text-gold/70 mx-auto mb-4" />
                <p className="font-display text-xl text-white/90 mb-2">Tasting notes are members-only</p>
                <p className="font-body text-sm text-white/50 max-w-md mx-auto mb-5">
                  Sommelier tasting characteristics, aroma profile and palate
                  notes are part of the AlfredWine member experience.
                </p>
                <div className="flex items-center gap-3 justify-center flex-wrap">
                  <Link to="/register">
                    <Button data-testid="tasting-register" className="bg-merlot hover:bg-merlot-light text-white font-body text-sm h-10 px-6 rounded-sm">
                      Create Free Account
                    </Button>
                  </Link>
                  <Link to="/login" className="font-body text-sm text-white/50 hover:text-white transition-colors" data-testid="tasting-signin">
                    Already a member? Sign in
                  </Link>
                </div>
              </div>
            )}
          </TabsContent>

        </Tabs>
      </div>
    </main>
  );
}
